import {
  VENDOR_IMPORT_MEDIA_TYPE_BY_FORMAT,
  type VendorImportFileFormat,
  type VendorImportMediaType,
} from '@clm/shared-types';

import type { ExtensionRefusal } from '../vendorImportCopy';

/**
 * What the browser decides about a chosen file before anything is sent —
 * CLM-113 (R-113-1, R-113-21; §4.7 "Choose a file").
 *
 * **The extension sets `media_type`; the server checks the bytes.** A browser
 * on Windows reports a `.csv` as `application/vnd.ms-excel`, so `File.type`
 * decides nothing here. The extension is matched case-insensitively. The four
 * spreadsheet formats CLM never reads are refused by name with the fix, so an
 * Admin holding a legacy `.xls` learns to re-save it without a round trip; a
 * password-protected `.xlsx` cannot be told from its name and is the server's
 * `ole_container`.
 *
 * **A file over the import bound is refused before it is sent**, naming the
 * bound: the server would answer a bare `413`, which carries no field error
 * (R-113-21). The bound comes from `limits`, read on load; while it is unknown
 * the server decides.
 */

export type FileChoice =
  | { readonly kind: 'accepted'; readonly format: VendorImportFileFormat; readonly media_type: VendorImportMediaType }
  | { readonly kind: 'refused_extension'; readonly refusal: ExtensionRefusal }
  | { readonly kind: 'refused_size'; readonly max_bytes: number };

const REFUSED_EXTENSIONS: Readonly<Record<string, ExtensionRefusal>> = {
  xls: 'xls',
  xlsm: 'xlsm',
  xlsb: 'xlsb',
  ods: 'ods',
};

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot + 1).trim().toLowerCase();
}

/** The format a file name's extension names, or `null`. */
export function formatOf(fileName: string): VendorImportFileFormat | null {
  const extension = extensionOf(fileName);
  return extension === 'xlsx' || extension === 'csv' ? extension : null;
}

export function chooseSheetFile(file: Pick<File, 'name' | 'size'>, maxBytes: number | null): FileChoice {
  const format = formatOf(file.name);
  if (format === null) {
    return { kind: 'refused_extension', refusal: REFUSED_EXTENSIONS[extensionOf(file.name)] ?? 'other' };
  }
  if (maxBytes !== null && file.size > maxBytes) return { kind: 'refused_size', max_bytes: maxBytes };
  return { kind: 'accepted', format, media_type: VENDOR_IMPORT_MEDIA_TYPE_BY_FORMAT[format] };
}

/**
 * `NotReadableError` — what a browser raises when a chosen `File` is re-read
 * after the file on disk changed or moved. On execute it means the bytes the
 * plan was made from are gone, so nothing is sent (§4.7).
 */
export function isNotReadable(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'NotReadableError';
}
